import { Head, Link } from '@inertiajs/react'
import { Plus, Server as ServerIcon, ChevronRight } from 'lucide-react'
import Heading from '@/components/heading'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import AppLayout from '@/layouts/app-layout'
import { serversPath, serverPath, newServerPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'
import type { Server } from '@/types'

type ServersIndexProps = {
  servers: (Server & { apps: { id: number; name: string }[] })[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: 'Servers', href: serversPath() },
]

export default function ServersIndex({ servers }: ServersIndexProps) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Servers" />

      <div className="p-6">
        <div className="flex items-center justify-between">
        <Heading
          title="Servers"
          description="Manage your servers and their applications"
        />
        <Button asChild>
          <Link href={newServerPath()}>
            <Plus className="mr-2 h-4 w-4" />
            Add Server
          </Link>
        </Button>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {servers.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ServerIcon className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No servers yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Get started by adding your first server
              </p>
              <Button asChild className="mt-4">
                <Link href={newServerPath()}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Server
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          servers.map((server) => (
            <Link key={server.id} href={serverPath(server.id)}>
              <Card className="group cursor-pointer transition-shadow hover:shadow-md">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <ServerIcon className="h-5 w-5" />
                      {server.name}
                    </CardTitle>
                    <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1" />
                  </div>
                  {server.hostname && (
                    <CardDescription>{server.hostname}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {server.apps.length} {server.apps.length === 1 ? 'app' : 'apps'}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))
        )}
      </div>
      </div>
    </AppLayout>
  )
}
